import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import PhoneInput, { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';
import OtpInput from 'react-otp-input';
import { Button, H4, Icons, IconSpinner, Modal } from '../../components';
import { useDispatch, useSelector } from 'react-redux';
import { sendOtpToMobile, verifyOneTimePassword } from '../../ducks/global.duck';
import { onTriggerError, onTriggerSuccess } from '../../util/toast';
import { currentUserSelector } from '../../ducks/user.duck';
import {
  getTextNotificationsOptedIn,
  getUserPhoneNumber,
  hidePhoneVerification,
  isPhoneNumberVerified,
} from '../../util/dataExtractors';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { useIntl } from 'react-intl';
import {
  selectProfileUpdateInProgress,
  updateProfile,
} from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { selectIsAuthenticated } from '../../ducks/auth.duck';

import '../../styles/react-phone.css';
import css from './PhoneVerificationModal.module.css';
import { PhoneVerificationSteps, StatusCode } from '../../util/enums';

const TWILIO_FLOW = process.env.REACT_APP_TWILIO_FLOW === 'true';

const PhoneVerificationModal = ({
  isOpen = false,
  onClose = () => {},
  defaultCurrentStep = 0,
  defaultPhoneNumber,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  // undefined means this opening of the modal has not been initialized yet;
  // an empty string means the user intentionally cleared the field.
  const [phoneNumber, setPhoneNumber] = useState(undefined);
  const [otp, setOtp] = useState('');
  const [currentStep, setCurrentStep] = useState(PhoneVerificationSteps?.Step_1);
  const [errorMessage, setErrorMessage] = useState('');

  // single source of truth: user preference (false = opt-in, true = opt-out)
  const [isTextOptOutChecked, setIsTextOptOutChecked] = useState(false);

  const { otpLoading, verifyLoading } = useSelector(state => state.global);
  const currentUser = useSelector(currentUserSelector);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const updateInProgress = useSelector(selectProfileUpdateInProgress);
  const isPhoneVerified = isPhoneNumberVerified(currentUser);
  const savedPhoneNumberMaybe = getUserPhoneNumber(currentUser);
  const hidePhoneVerificationModal = hidePhoneVerification(currentUser);
  const isModalOpen =
    isAuthenticated && ((!hidePhoneVerificationModal && !isPhoneVerified) || isOpen);
  const initializedForCurrentOpen = useRef(false);

  useEffect(() => {
    if (!isModalOpen) {
      initializedForCurrentOpen.current = false;
      return;
    }

    // Wait for asynchronous account data, then initialize exactly once per opening.
    // Subsequent currentUser object refreshes must not overwrite in-progress edits.
    if (!currentUser?.id || initializedForCurrentOpen.current) {
      return;
    }

    const initialPhoneNumber =
      typeof defaultPhoneNumber === 'undefined' ? savedPhoneNumberMaybe || '' : defaultPhoneNumber;
    setCurrentStep(defaultCurrentStep ?? PhoneVerificationSteps?.Step_1);
    setPhoneNumber(initialPhoneNumber);
    setIsTextOptOutChecked(!getTextNotificationsOptedIn(currentUser));
    initializedForCurrentOpen.current = true;
  }, [currentUser, defaultCurrentStep, defaultPhoneNumber, isModalOpen, savedPhoneNumberMaybe]);

  // Save on Step 1
  const handlePhoneNumberSubmit = async () => {
    // Validate phone number if provided, even when opting out
    if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
      onTriggerError(intl.formatMessage({ id: 'PhoneNumberModal.invalidPhoneNumber' }));
      return;
    }

    // If opting OUT, save preferences and phone number (if valid)
    if (isTextOptOutChecked) {
      try {
        const normalizedPhoneNumber = phoneNumber || null;
        const phoneNumberChanged = normalizedPhoneNumber !== (savedPhoneNumberMaybe || null);
        const phoneNumberRemoved = normalizedPhoneNumber === null;
        const savedNotifications =
          currentUser?.attributes?.profile?.protectedData?.notifications || {};
        const updatedUser = await dispatch(
          updateProfile({
            protectedData: {
              textNotificationsOptedIn: false,
              hidePhoneVerificationModal: true,
              hideTextNotificationsOptedInModal: true,
              phoneNumber: normalizedPhoneNumber,
              ...(phoneNumberRemoved
                ? { isPhoneNumberVerified: null }
                : phoneNumberChanged
                ? { isPhoneNumberVerified: false }
                : {}),
              ...(phoneNumberRemoved
                ? { notifications: { ...savedNotifications, phone: false } }
                : {}),
            },
          })
        );
        if (!updatedUser) {
          throw new Error('Phone preference update failed.');
        }
        onTriggerSuccess(
          intl.formatMessage({ id: 'PhoneNumberModal.preferencesSaved' }) || 'Changes saved'
        );
        onClose();
        setCurrentStep(PhoneVerificationSteps?.Step_1);
        return;
      } catch (e) {
        onTriggerError(
          intl.formatMessage({ id: 'PhoneNumberModal.preferencesSaveFailed' }) ||
            'Failed to save changes'
        );
        return;
      }
    }

    // If opting IN, validate phone and proceed with Twilio/non-Twilio flow
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      onTriggerError(intl.formatMessage({ id: 'PhoneNumberModal.invalidPhoneNumber' }));
      return;
    }

    try {
      if (TWILIO_FLOW) {
        // Send OTP
        const data = await dispatch(sendOtpToMobile({ phone: phoneNumber }));
        if (data?.statusCode === StatusCode?.SUCCESS) {
          onTriggerSuccess(intl.formatMessage({ id: 'PhoneNumberModal.otpSentSuccess' }));
          setCurrentStep(PhoneVerificationSteps?.Step_2);
        } else {
          onTriggerError(data?.message);
        }
      } else {
        // Direct save without OTP
        const updatedUser = await dispatch(
          updateProfile({
            protectedData: {
              isPhoneNumberVerified: false,
              hidePhoneVerificationModal: true,
              phoneNumber: phoneNumber,
              textNotificationsOptedIn: true,
            },
          })
        );
        if (!updatedUser) {
          throw new Error('Phone number update failed.');
        }
        onTriggerSuccess(intl.formatMessage({ id: 'PhoneNumberModal.phoneNumberSaved' }));
        onClose();
        setPhoneNumber('');
        setCurrentStep(PhoneVerificationSteps?.Step_1);
      }
    } catch (error) {
      onTriggerError(
        TWILIO_FLOW
          ? intl.formatMessage({ id: 'PhoneNumberModal.otpSendFailed' })
          : intl.formatMessage({ id: 'PhoneNumberModal.phoneNumberSaveFailed' })
      );
    }
  };

  // Verify OTP on Step 2 (only relevant when opting IN)
  const handleOtpSubmit = async () => {
    try {
      const data = await dispatch(
        verifyOneTimePassword({ phone: phoneNumber, otp: otp }, { textNotificationsOptedIn: true })
      );
      if (data?.statusCode === StatusCode?.SUCCESS) {
        onTriggerSuccess(intl.formatMessage({ id: 'PhoneNumberModal.otpVerifiedSuccess' }));
        onClose();
        setOtp('');
        setPhoneNumber('');
        setCurrentStep(PhoneVerificationSteps?.Step_1);
      } else {
        onTriggerError(data?.message);
      }
    } catch (error) {
      onTriggerError(intl.formatMessage({ id: 'PhoneNumberModal.otpVerifyFailed' }));
    }
  };

  const onCloseModal = () => {
    onClose();
    // Closing the explicit Account Settings editor is a cancel action. The
    // automatically shown onboarding prompt still records that it was dismissed.
    if (!isOpen) {
      dispatch(
        updateProfile({
          protectedData: {
            hidePhoneVerificationModal: true,
            hideTextNotificationsOptedInModal: true,
            textNotificationsOptedIn: !isTextOptOutChecked,
          },
        })
      );
    }
  };

  // Button disabled logic:
  // - If opting out => enabled (unless loading)
  // - If opting in => need valid phone
  const isPhoneValid = phoneNumber && isValidPhoneNumber(phoneNumber);
  const disablePrimary =
    updateInProgress ||
    otpLoading ||
    (currentStep === PhoneVerificationSteps.Step_1 &&
    !isTextOptOutChecked && // opting in
      !isPhoneValid);

  const renderStepContent = step => {
    // If TWILIO_FLOW is false, we only need the phone number input step
    if (!TWILIO_FLOW && step === 1) return null;

    switch (step) {
      case 0:
        return (
          <div className={css.verificationCodeWrapper}>
            <H4 as="h4">{intl.formatMessage({ id: 'PhoneNumberModal.title' })}</H4>
            <p className={css.codeDescription}>
              {intl.formatMessage({ id: 'PhoneNumberModal.body' })}
            </p>

            {/* Phone field shown always, but only required if opting in */}
            <div className={css.phoneNumberWrapper}>
              <label className={css.phoneNumberLabel}>
                {intl.formatMessage({ id: 'PhoneNumberModal.phoneNumberLabel' })}
              </label>
              <PhoneInput
                format={formatPhoneNumberIntl}
                className={css.fieldTextInput}
                placeholder={intl.formatMessage({
                  id: 'PhoneNumberModal.phoneNumberPlaceholder',
                })}
                defaultCountry="US"
                countries={['US', 'CA']}
                value={phoneNumber}
                onChange={value => {
                  const nextPhoneNumber = value ?? '';
                  setPhoneNumber(nextPhoneNumber);
                  if (!nextPhoneNumber) {
                    // No number means SMS cannot be delivered.
                    setIsTextOptOutChecked(true);
                  }
                  const formattedNumber = nextPhoneNumber
                    ? formatPhoneNumberIntl(nextPhoneNumber)
                    : '';
                  if (!nextPhoneNumber || isValidPhoneNumber(formattedNumber)) {
                    setErrorMessage('');
                  } else {
                    setErrorMessage(
                      intl.formatMessage({ id: 'PhoneNumberModal.invalidPhoneNumber' })
                    );
                  }
                }}
              />
            </div>

            {/* Radio group: Opt-in OR Opt-out (mutually exclusive) */}
            <fieldset className={css.radioGroup} aria-labelledby="sms-pref-title">
              <legend id="sms-pref-title" className={css.radioLegend}>
                {intl.formatMessage({ id: 'PhoneNumberModal.notificationPreference' }) ||
                  'Text message preference'}
              </legend>

              {/* OPT-IN */}
              <label
                htmlFor="textOptIn"
                className={`${css.radioCard} ${!isTextOptOutChecked ? css.radioCardChecked : ''}`}
              >
                <input
                  type="radio"
                  name="textPref"
                  id="textOptIn"
                  checked={!isTextOptOutChecked}
                  onChange={() => {
                    setIsTextOptOutChecked(false);
                    if (!phoneNumber) {
                      setErrorMessage(
                        intl.formatMessage({ id: 'PhoneNumberModal.invalidPhoneNumber' })
                      );
                    }
                  }}
                  className={css.radioInput}
                />
                <span className={css.radioControl} aria-hidden="true">
                  <span className={css.radioDot} />
                </span>
                <span className={css.radioContent}>
                  <span className={css.radioTitle}>
                    {intl.formatMessage({ id: 'PhoneNumberModal.optInTitle' }) ||
                      'Receive booking updates via SMS'}
                  </span>
                  <span className={css.radioDesc}>
                    {intl.formatMessage({ id: 'PhoneNumberModal.optInDesc' }) ||
                      'We’ll text you confirmations and important updates. Msg & data rates may apply.'}{' '}
                    <a href="/terms-of-service" target="_blank" rel="noreferrer">
                      {intl.formatMessage({ id: 'PhoneNumberModal.termsOfService' }) || 'Terms'}
                    </a>{' '}
                    {intl.formatMessage({ id: 'PhoneNumberModal.and' }) || 'and'}{' '}
                    <a href="/privacy-policy" target="_blank" rel="noreferrer">
                      {intl.formatMessage({ id: 'PhoneNumberModal.privacyPolicy' }) || 'Privacy'}
                    </a>
                    .
                  </span>
                </span>
              </label>

              {/* OPT-OUT */}
              <label
                htmlFor="textOptOut"
                className={`${css.radioCard} ${isTextOptOutChecked ? css.radioCardChecked : ''}`}
              >
                <input
                  type="radio"
                  name="textPref"
                  id="textOptOut"
                  checked={isTextOptOutChecked}
                  onChange={() => {
                    setIsTextOptOutChecked(true);
                    if (!phoneNumber) {
                      setErrorMessage('');
                    }
                  }}
                  className={css.radioInput}
                />
                <span className={css.radioControl} aria-hidden="true">
                  <span className={css.radioDot} />
                </span>
                <span className={css.radioContent}>
                  <span className={css.radioTitle}>
                    {intl.formatMessage({ id: 'PhoneNumberModal.optOutTitle' }) ||
                      'Opt out of text notifications'}
                  </span>
                  <span className={css.radioDesc}>
                    {intl.formatMessage({ id: 'PhoneNumberModal.optOutDesc' }) ||
                      'You won’t receive SMS alerts. You can change this later in Settings.'}
                  </span>
                </span>
              </label>
            </fieldset>

            <div className={css.btnWrapper}>
              <Button onClick={handlePhoneNumberSubmit} disabled={disablePrimary}>
                {otpLoading ? <IconSpinner /> : 'Save changes'}
              </Button>
            </div>
          </div>
        );

      case 1:
        return (
          <div className={css.verificationCodeWrapper}>
            <label className={css.verificationCodeLabel}>
              {intl.formatMessage({ id: 'PhoneNumberModal.enterCodeLabel' })}
            </label>
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={6}
              shouldAutoFocus
              isInputNum
              containerStyle={css.otpContainer}
              inputStyle={css.otpInput}
              renderInput={props => <input {...props} />}
            />
            <div className={css.btnWrapper}>
              <Button onClick={handleOtpSubmit} disabled={!otp || verifyLoading}>
                {verifyLoading ? (
                  <IconSpinner />
                ) : (
                  intl.formatMessage({ id: 'PhoneNumberModal.verifyOtpBtn' })
                )}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      id="VerifyPhoneNumber"
      isOpen={isModalOpen}
      onClose={onCloseModal}
      onManageDisableScrolling={(componentId, disableScrolling) =>
        dispatch(manageDisableScrolling(componentId, disableScrolling))
      }
    >
      <div className={css.verificationWrapper}>
        {updateInProgress ? (
          <div className={css.spinnerWrapper}>
            <IconSpinner />
          </div>
        ) : (
          <div className={css.verifyForm}>
            {renderStepContent(currentStep)}
            {errorMessage && (
              <div className={css.errorWrapper}>
                <Icons name="error" />
                <p className={css.errorMessage}>{errorMessage}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

PhoneVerificationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  defaultCurrentStep: PropTypes.number,
  defaultPhoneNumber: PropTypes.string,
};

export default PhoneVerificationModal;
